import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Form, Button, Row, Col, Card, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  createHousingData,
  updateHousingData,
  getHousingPhoto,
} from "../../services/housingService";
import {
  getDistrict,
  getSubdistrictByDistrict,
} from "../../services/commonService";
import { useHelp } from "../../hooks/UseHelp";
import HelpIcon from "../../hooks/HelpIcon";
// import { compressImage } from "../../utils/image-compression-and-integration";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
// import PictureEmbed from "./PictureEmbed";

// Fix default marker icon (Leaflet issue in React)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const baseLayers = {
  osm: {
    name: "🌍 OpenStreetMap",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: "© OpenStreetMap",
  },
  satellite: {
    name: "🛰️ Esri Satelit",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "© Esri",
  },
  hybrid: {
    name: "🛰️+🛣️ Esri Hybrid",
    type: "hybrid", // gabungan 2 TileLayer
  },
};

const HousingForm = ({ housing, onSuccess, onCancel }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  // const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null); // replace existing
  const [districts, setDistricts] = useState([]);
  const [subdistricts, setSubdistricts] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const { show, setShow, content, fetchHelp } = useHelp();
  const [showMapModal, setShowMapModal] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [activeBase, setActiveBase] = useState("hybrid");
  const [gpsStatus, setGpsStatus] = useState("checking");
  // "checking" | "active" | "inactive"

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm();

  // Effect pertama: ambil data districts saat load awal
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const data = await getDistrict();
        setDistricts(data.data);
      } catch (error) {
        console.error("Gagal ambil district:", error);
      }
    };
    fetchDistricts();
  }, []);

  useEffect(() => {
    if (housing) {
      // Pre-fill form with existing data
      Object.keys(housing).forEach((key) => {
        // console.log("key :", key);
        if (housing[key] !== null && housing[key] !== undefined) {
          setValue(key, housing[key]);
          // console.log("housing[key]:", housing[key]);
        }
      });
    }
  }, [housing, setValue]);

  useEffect(() => {
    if (housing && districts.length > 0) {
      setValue("district_id", housing.district_id);
      setSelectedDistrict(housing.district_id);
    }
  }, [housing, districts, setValue]);

  // Effect kedua: ambil subdistrict tiap kali district berubah
  useEffect(() => {
    const fetchSubdistricts = async () => {
      if (selectedDistrict) {
        try {
          const data = await getSubdistrictByDistrict(selectedDistrict);
          setSubdistricts(data.data);
        } catch (error) {
          console.error("Gagal ambil subdistrict:", error);
        }
      } else {
        setSubdistricts([]);
        setValue("sub_district_id", "");
      }
    };
    fetchSubdistricts();
  }, [selectedDistrict, setValue]);

  useEffect(() => {
    if (housing && subdistricts.length > 0) {
      setValue("sub_district_id", housing.sub_district_id);
    }
  }, [housing, subdistricts, setValue]);

  useEffect(() => {
    let objectUrl;
    const loadPhoto = async () => {
      if (housing && housing.photo_before_repair) {
        try {
          const url = await getHousingPhoto(housing.photo_before_repair);
          objectUrl = url; // simpan sementara buat dibersihin nanti
          setPhotoPreview(url);
        } catch (err) {
          console.error("Gagal ambil foto rumah:", err);
        }
      } else {
        setPhotoPreview(null); // mode tambah data baru
      }
    };

    loadPhoto();

    return () => {
      // cleanup: hapus blob URL lama dari memory
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [housing]);

  useEffect(() => {
    return () => {
      if (photoPreview && photoPreview.startsWith("blob:")) {
        URL.revokeObjectURL(photoPreview);
      }
    };
  }, [photoPreview]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setGpsStatus("inactive");
      toast.warn("Perangkat ini tidak mendukung GPS/Geolocation.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      () => {
        setGpsStatus("active");
      },
      (error) => {
        // console.log("GPS error:", error);
        setGpsStatus("inactive");
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.warn(
              "Izin lokasi ditolak. Aktifkan izin lokasi di browser/HP Anda."
            );
            break;
          case error.POSITION_UNAVAILABLE:
            toast.warn(
              "Lokasi tidak tersedia. Pastikan GPS di perangkat aktif."
            );
            break;
          case error.TIMEOUT:
            toast.warn("Tidak bisa mengakses lokasi: waktu tunggu habis.");
            break;
          default:
            toast.warn("Tidak bisa mengakses lokasi. Periksa pengaturan GPS.");
        }
      }
    );
  }, []);

  // const handleGetLocation = () => {
  //   if (!navigator.geolocation) {
  //     toast.error("Geolocation tidak didukung di perangkat ini");
  //     return;
  //   }

  //   navigator.geolocation.getCurrentPosition(
  //     (position) => {
  //       const { latitude, longitude } = position.coords;
  //       const lat = latitude.toFixed(8);
  //       const lng = longitude.toFixed(8);
  //       setValue("latitude", lat);
  //       setValue("longitude", lng);
  //       setCurrentLocation({ lat, lng });
  //       toast.success("Lokasi berhasil diambil");
  //     },
  //     (error) => {
  //       console.error("Error get location:", error);

  //       switch (error.code) {
  //         case error.PERMISSION_DENIED:
  //           toast.error(
  //             "Izin lokasi ditolak. Aktifkan izin lokasi di browser/HP Anda."
  //           );
  //           break;
  //         case error.POSITION_UNAVAILABLE:
  //           toast.error(
  //             "Lokasi tidak tersedia. Pastikan GPS di perangkat aktif."
  //           );
  //           break;
  //         case error.TIMEOUT:
  //           toast.error("Gagal mendapatkan lokasi: waktu tunggu habis.");
  //           break;
  //         default:
  //           toast.error("Gagal mengambil lokasi. Periksa pengaturan GPS.");
  //       }
  //     },
  //     { timeout: 10000 } // max tunggu 10 detik
  //   );
  // };

  // const handleOpenMap = () => {
  //   const lat = getValues("latitude");
  //   const lng = getValues("longitude");
  //   if (lat && lng) {
  //     const url = `https://www.google.com/maps/place/${lat},${lng}/@${lat},${lng},18z/data=!3m1!1e3`;
  //     window.open(url, "_blank");
  //   } else {
  //     toast.error("Isi latitude & longitude dulu");
  //   }
  // };

  const DraggableMarker = ({ position, setPosition }) => {
    useMapEvents({
      click(e) {
        setPosition(e.latlng);
      },
    });

    return (
      <Marker
        position={position}
        draggable={true}
        eventHandlers={{
          dragend: (e) => {
            const { lat, lng } = e.target.getLatLng();
            setPosition({ lat, lng });
          },
        }}
      />
    );
  };

  const handleOpenMapModal = () => {
    const lat = parseFloat(getValues("latitude")) || 4.004526147294771; // fallback koordinat 0 Talaud
    const lng = parseFloat(getValues("longitude")) || 126.68266121024419; //
    setCurrentLocation({ lat, lng });
    setShowMapModal(true);
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      const formData = new FormData();

      // Append all form data
      Object.keys(data).forEach((key) => {
        // console.log("data key: ", key, "data key: ", data[key]);
        if (data[key] !== null && data[key] !== undefined) {
          formData.append(key, data[key]);
        }
      });

      if (housing) {
        await updateHousingData(housing.housing_id, formData);
        // console.log(
        //   "housing.housing_id, formData",
        //   housing.housing_id,
        //   formData
        // ); // debug
        toast.success("Data berhasil diperbarui");
      } else {
        // console.log("creating housing data");
        await createHousingData(formData);
        // console.log(tempo2);  // debug
        toast.success("Data berhasil ditambahkan");
      }
      onSuccess();
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Gagal menyimpan data");
    } finally {
      setIsSubmitting(false);
    }
  };

  // const handlePhotoChange = async (e) => {
  //   const file = e.target.files?.[0];
  //   if (!file) {
  //     setPhotoFile(null);
  //     setPhotoPreview(null);
  //     return;
  //   }

  //   try {
  //     // compress to <= 1 MB
  //     const compressed = await compressImage(file, {
  //       maxSizeMB: 1,
  //       maxWidth: 1600,
  //       maxHeight: 1600,
  //     });
  //     setPhotoFile(compressed);
  //     // show preview from blob URL (faster than base64)
  //     const url = URL.createObjectURL(compressed);
  //     setPhotoPreview(url);
  //   } catch (err) {
  //     console.error("Gagal kompres gambar:", err);
  //     // fallback to original
  //     setPhotoFile(file);
  //     const url = URL.createObjectURL(file);
  //     setPhotoPreview(url);
  //   }
  // };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col md={12}>
          <Card className="mb-3">
            <Card.Header>
              <h5>WILAYAH</h5>
            </Card.Header>
            <Card.Body className="d-md-flex gap-4 me-4">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Kecamatan *</Form.Label>
                  <Form.Select
                    {...register("district_id", {
                      required: "Kecamatan wajib dipilih",
                    })}
                    isInvalid={!!errors.district_id}
                    onChange={(e) => setSelectedDistrict(e.target.value)}
                  >
                    <option value="">Pilih...</option>
                    {districts.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.district_id?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Desa/Kelurahan *</Form.Label>
                  <Form.Select
                    {...register("sub_district_id", {
                      required: "Desa/kelurahan wajib dipilih",
                    })}
                    isInvalid={!!errors.sub_district_id}
                    disabled={!selectedDistrict}
                  >
                    <option value="">Pilih...</option>
                    {subdistricts.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.sub_district_id?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <Card className="mb-3">
            <Card.Header>
              <h5>ADMINISTRASI</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Nama Kepala Keluarga *</Form.Label>
                    <Form.Control
                      type="text"
                      {...register("household_head_name", {
                        required: "Nama kepala keluarga wajib diisi",
                      })}
                      isInvalid={!!errors.household_head_name}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.household_head_name?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Nomor Kartu Keluarga</Form.Label>
                    <Form.Control
                      type="text"
                      {...register("family_card_number")}
                    />
                  </Form.Group>
                  {/* <Form.Group className="mb-3">
                <Form.Label>Nomor Kartu Keluarga</Form.Label>
                <Form.Control
                  type="text"
                  {...register('family_card_number', { 
                    pattern: {
                      value: /^\d{16}$/,
                      message: 'Nomor KK harus 16 digit'
                    }
                  })}
                  isInvalid={!!errors.family_card_number}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.family_card_number?.message}
                </Form.Control.Feedback>
              </Form.Group> */}

                  {/* <Form.Group className="mb-3">
                    <Form.Label>NIK</Form.Label>
                    <Form.Control type="text" {...register("nik")} />
                  </Form.Group> */}

                  <Form.Group className="mb-3">
                    <Form.Label>NIK</Form.Label>
                    <Form.Control
                      type="text"
                      {...register("nik", {
                        required: "NIK wajib diisi",
                        pattern: {
                          value: /^\d{16}$/,
                          message: "NIK harus 16 digit",
                        },
                      })}
                      isInvalid={!!errors.nik}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.nik?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Jumlah (KK) *</Form.Label>
                    <Form.Control
                      type="text"
                      {...register("family_members_count")}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Jumlah Penghuni *</Form.Label>
                    <Form.Control
                      type="text"
                      {...register("occupants_count")}
                    />
                  </Form.Group>

                  {/* <Form.Group className="mb-3">
                <Form.Label>Jumlah Anggota Keluarga (Dalam KK) *</Form.Label>
                <Form.Control
                  type="text"
                  {...register('family_members_count', { 
                     pattern: {
                      value: /^([0-9]{1,2})(\.[0-9]+)?$/,
                      message: 'Jumlah KK wajib diisi'
                    }
                  })}
                  isInvalid={!!errors.family_members_count}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.family_members_count?.message}
                </Form.Control.Feedback>
              </Form.Group>
 */}
                  <Form.Group className="mb-3">
                    <Form.Label>Alamat *</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      {...register("address")}
                    />
                  </Form.Group>

                  {/* <Form.Group className="mb-3">
                <Form.Label>Alamat *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  {...register('address', { required: 'Alamat wajib diisi' })}
                  isInvalid={!!errors.address}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.address?.message}
                </Form.Control.Feedback>
              </Form.Group>
 */}
                  <Form.Group className="mb-3">
                    <Form.Label>Umur (tahun) *</Form.Label>
                    <Form.Control type="text" {...register("age")} />
                  </Form.Group>
                  {/* <Form.Group className="mb-3">
                <Form.Label>Umur (tahun) *</Form.Label>
                <Form.Control
                  type="text"
                  {...register('age', { 
                    pattern: {
                      value: /^([0-9]{1,2})(\.[0-9]+)?$/,
                      message: 'Umur wajib diisi'
                    }
                  })}
                  isInvalid={!!errors.age}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.age?.message}
                </Form.Control.Feedback>
              </Form.Group> */}

                  <Form.Group className="mb-3">
                    <Form.Label>Pendidikan Terakhir *</Form.Label>
                    <Form.Select {...register("education")}>
                      <option value="">Pilih...</option>
                      <option value="TIDAK PUNYA IJAZAH">
                        TIDAK PUNYA IJAZAH
                      </option>
                      <option value="SD/SEDERAJAT">SD/SEDERAJAT</option>
                      <option value="SMP/SEDERAJAT">SMP/SEDERAJAT</option>
                      <option value="SMA/SEDERAJAT">SMA/SEDERAJAT</option>
                      <option value="SMA">SMA</option>
                      <option value="D1/D2/D3">D1/D2/D3</option>
                      <option value="D4/S1">D4/S1</option>
                    </Form.Select>
                  </Form.Group>
                  {/* <Form.Group className="mb-3">
                <Form.Label>Pendidikan Terakhir *</Form.Label>
                <Form.Select
                  {...register('education', { required: 'Pendidikan terakhir wajib dipilih' })}
                  isInvalid={!!errors.education}
                >
                  <option value="">Pilih...</option>
                  <option value="TIDAK PUNYA IJAZAH">Tidak Punya Ijazah</option>
                  <option value="SD/SEDERAJAT">SD/SEDERAJAT</option>
                  <option value="SMP/SEDERAJAT">SMP/SEDERAJAT</option>
                  <option value="SMA/SEDERAJAT">SMA/SEDERAJAT</option>
                  <option value="SMA">SMA</option>
                  <option value="D1/D2/D3">D1/D2/D3</option>
                  <option value="D4/S1">D4/S1</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.education?.message}
                </Form.Control.Feedback>
              </Form.Group> */}
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Jenis Kelamin *</Form.Label>
                    <Form.Select
                      {...register("gender")}
                      // isInvalid={!!errors.gender}
                    >
                      {/* <Form.Select
                  {...register('gender', { required: 'Jenis kelamin wajib dipilih' })}
                  isInvalid={!!errors.gender}
                > */}
                      <option value="">Pilih...</option>
                      <option value="LAKI-LAKI">LAKI-LAKI</option>
                      <option value="PEREMPUAN">PEREMPUAN</option>
                    </Form.Select>
                    {/* <Form.Control.Feedback type="invalid">
                  {errors.gender?.message}
                </Form.Control.Feedback> */}
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Pekerjaan *</Form.Label>
                    <Form.Select {...register("occupation")}>
                      <option value="">Pilih...</option>
                      <option value="PNS">PNS</option>
                      <option value="BUMN/D">BUMN/D</option>
                      <option value="TNI/POLRI">TNI/POLRI</option>
                      <option value="KARYAWAN">KARYAWAN</option>
                      <option value="WIRAUSAHA">WIRAUSAHA</option>
                      <option value="PETANI">PETANI</option>
                      <option value="BURUH HARIAN">BURUH HARIAN</option>
                      <option value="NELAYAN">NELAYAN</option>
                      <option value="OJEK/SOPIR">OJEK/SOPIR</option>
                      <option value="PRAMUWISMA">PRAMUWISMA</option>
                      <option value="LANSIA/IRT">LANSIA/IRT</option>
                      <option value="TUKANG/MONTIR">TUKANG/MONTIR</option>
                      <option value="HONORER">HONORER</option>
                      <option value="PENSIUNAN">PENSIUNAN</option>
                      <option value="LAINNYA">LAINNYA</option>
                      <option value="TIDAK BEKERJA">TIDAK BEKERJA</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Penghasilan per Bulan *</Form.Label>
                    <Form.Select {...register("monthly_income")}>
                      <option value="">Pilih...</option>
                      <option value="0 - 1,2 JUTA">0 - 1,2 JUTA</option>
                      <option value="1,3 - 1,8 JUTA">1,3 - 1,8 JUTA</option>
                      <option value="1,9 - 2,1 JUTA">1,9 - 2,1 JUTA</option>
                      <option value="2,2 - 2,6 JUTA">2,2 - 2,6 JUTA</option>
                      <option value="2,7 - 3,1 JUTA">2,7 - 3,1 JUTA</option>
                      <option value="3,2 - 3,6 JUTA">3,2 - 3,6 JUTA</option>
                      <option value="3,7 - 4,2 JUTA">3,7 - 4,2 JUTA</option>
                      <option value="> 4,2 JUTA">LEBIH DARI 4,2 JUTA</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Status Kepemilikan Rumah *</Form.Label>
                    <Form.Select {...register("house_ownership")}>
                      <option value="">Pilih...</option>
                      <option value="MILIK SENDIRI">MILIK SENDIRI</option>
                      <option value="BUKAN MILIK SENDIRI">
                        BUKAN MILIK SENDIRI
                      </option>
                      <option value="KONTRAK/SEWA">KONTRAK/SEWA</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Aset Rumah di Lokasi Lain *</Form.Label>
                    <Form.Select {...register("house_assets_other_locations")}>
                      <option value="">Pilih...</option>
                      <option value="ADA">ADA</option>
                      <option value="TIDAK ADA">TIDAK ADA</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Status Kepemilikan Tanah *</Form.Label>
                    <Form.Select {...register("land_ownership")}>
                      <option value="">Pilih...</option>
                      <option value="MILIK SENDIRI">MILIK SENDIRI</option>
                      <option value="BUKAN MILIK SENDIRI">
                        BUKAN MILIK SENDIRI
                      </option>
                      <option value="TANAH NEGARA">TANAH NEGARA</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Aset Lahan di Lokasi Lain *</Form.Label>
                    <Form.Select {...register("land_assets_other_locations")}>
                      <option value="">Pilih...</option>
                      <option value="ADA">ADA</option>
                      <option value="TIDAK ADA">TIDAK ADA</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Sumber Penerangan *</Form.Label>
                    <Form.Select {...register("lighting_source")}>
                      <option value="">Pilih...</option>
                      <option value="LISTRIK PLN DENGAN METERAN">
                        LISTRIK PLN DENGAN METERAN
                      </option>
                      <option value="LISTRIK PLN TANPA METERAN">
                        LISTRIK PLN TANPA METERAN
                      </option>
                      <option value="LISTRIK NON PLN">LISTRIK NON PLN</option>
                      <option value="BUKAN LISTRIK">BUKAN LISTRIK</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Bantuan Perumahan *</Form.Label>
                    <Form.Select {...register("housing_assistance")}>
                      <option value="">Pilih...</option>
                      <option value="YA, > 10 TAHUN YANG LALU">
                        {"YA, > 10 TAHUN YANG LALU"}
                      </option>
                      <option value="YA, < 10 TAHUN YANG LALU">
                        {"YA, < 10 TAHUN YANG LALU"}
                      </option>
                      <option value="BELUM PERNAH">BELUM PERNAH</option>
                    </Form.Select>
                  </Form.Group>

                  {/* <Form.Group className="mb-3">
                <Form.Label>Pekerjaan *</Form.Label>
                <Form.Select
                  {...register('occupation', { required: 'Pekerjaan wajib dipilih' })}
                  isInvalid={!!errors.occupation}
                >
                  <option value="">Pilih...</option>
                  <option value="PNS">PNS</option>
                  <option value="BUMN/D">BUMN/D</option>
                  <option value="TNI/POLRI">TNI/POLRI</option>
                  <option value="KARYAWAN">KARYAWAN</option>
                  <option value="WIRAUSAHA">WIRAUSAHA</option>
                  <option value="PETANI">PETANI</option>
                  <option value="BURUH HARIAN">BURUH HARIAN</option>
                  <option value="NELAYAN">NELAYAN</option>
                  <option value="OJEK/SOPIR">OJEK/SOPIR</option>
                  <option value="PRAMUWISMA">PRAMUWISMA</option>
                  <option value="LANSIA/IRT">LANSIA/IRT</option>
                  <option value="TUKANG/MONTIR">TUKANG/MONTIR</option>
                  <option value="HONORER">HONORER</option>
                  <option value="PENSIUNAN">PENSIUNAN</option>
                  <option value="LAINNYA">LAINNYA</option>
                  <option value="TIDAK BEKERJA">TIDAK BEKERJA</option>

                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.occupation?.message}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Penghasilan per Bulan *</Form.Label>
                <Form.Select
                  {...register('monthly_income', { required: 'Penghasilan wajib dipilih' })}
                  isInvalid={!!errors.monthly_income}
                >
                  <option value="">Pilih...</option>
                  <option value="0 - 1,2 JUTA">0 - 1,2 JUTA</option>
                  <option value="1,3 - 1,8 JUTA">1,3 - 1,8 JUTA</option>
                  <option value="1,9 - 2,1 JUTA">1,9 - 2,1 JUTA</option>
                  <option value="2,2 - 2,6 JUTA">2,2 - 2,6 JUTA</option>
                  <option value="2,7 - 3,1 JUTA">2,7 - 3,1 JUTA</option>
                  <option value="3,2 - 3,6 JUTA">3,2 - 3,6 JUTA</option>
                  <option value="3,7 - 4,2 JUTA">3,7 - 4,2 JUTA</option>
                  <option value="> 4,2 JUTA">{">"} 4,2 JUTA</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.monthly_income?.message}
                </Form.Control.Feedback>
              </Form.Group>

              
              <Form.Group className="mb-3">
                <Form.Label>Status Kepemilikan Rumah *</Form.Label>
                <Form.Select
                  {...register('house_ownership', { required: 'Status kepemilikan rumah wajib dipilih' })}
                  isInvalid={!!errors.house_ownership}
                >
                  <option value="">Pilih...</option>
                  <option value="MILIK SENDIRI">MILIK SENDIRI</option>
                  <option value="BUKAN MILIK SENDIRI">BUKAN MILIK SENDIRI</option>
                  <option value="KONTRAK/SEWA">KONTRAK/SEWA</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.house_ownership?.message}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Aset Rumah di Lokasi Lain *</Form.Label>
                <Form.Select
                  {...register('house_assets_other_locations', { required: 'Aset rumah di lokasi lain wajib dipilih' })}
                  isInvalid={!!errors.house_assets_other_locations}
                >
                  <option value="">Pilih...</option>
                  <option value="ADA">Ada</option>
                  <option value="TIDAK ADA">Tidak Ada</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.house_assets_other_locations?.message}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Status Kepemilikan Tanah *</Form.Label>
                <Form.Select
                  {...register('land_ownership', { required: 'Status kepemilikan tanah wajib dipilih' })}
                  isInvalid={!!errors.land_ownership}
                >
                  <option value="">Pilih...</option>
                  <option value="MILIK SENDIRI">Milik Sendiri</option>
                  <option value="BUKAN MILIK SENDIRI">Bukan Milik Sendiri</option>
                  <option value="TANAH NEGARA">Tanah Negara</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.land_ownership?.message}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Sumber Penerangan *</Form.Label>
                <Form.Select
                  {...register('lighting_source', { required: 'Sumber penerangan wajib dipilih' })}
                  isInvalid={!!errors.lighting_source}
                >
                  <option value="">Pilih...</option>
                  <option value="LISTRIK PLN DENGAN METERAN">LISTRIK PLN DENGAN METERAN</option>
                  <option value="LISTRIK PLN TANPA METERAN">LISTRIK PLN TANPA METERAN</option>
                  <option value="LISTRIK NON PLN">LISTRIK NON PLN</option>
                  <option value="BUKAN LISTRIK">BUKAN LISTRIK</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.lighting_source?.message}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Bantuan Perumahan *</Form.Label>
                <Form.Select
                  {...register('housing_assistance', { required: 'Bantuan perumahan wajib dipilih' })}
                  isInvalid={!!errors.housing_assistance}
                >
                  <option value="">Pilih...</option>
                  <option value="YA, > 10 TAHUN YANG LALU">{"YA, > 10 TAHUN YANG LALU"}</option>
                  <option value="YA, < 10 TAHUN YANG LALU">{"YA, < 10 TAHUN YANG LALU"}</option>
                  <option value="BELUM PERNAH">BELUM PERNAH</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.housing_assistance?.message}
                </Form.Control.Feedback>
              </Form.Group> */}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Card className="mb-3">
            <Card.Header>
              <h5>DATA SPASIAL</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status Wilayah/Kawasan</Form.Label>
                    <Form.Check
                      {...register("disadvantaged_area")}
                      type="checkbox"
                      label="DAERAH TERTINGGAL, TERPENCIL"
                      value=""
                      id="disadvantaged_area"
                      // {...register('disadvantaged_area', { required: 'Kondisi fondasi wajib dipilih' })}
                      // isInvalid={!!errors.disadvantaged_area}
                    ></Form.Check>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      {...register("slum_area")}
                      type="checkbox"
                      label="KAWASAN KUMUH"
                      value=""
                      id="slum_area"
                      // {...register('slum_area', { required: 'Kondisi fondasi wajib dipilih' })}
                      // isInvalid={!!errors.slum_area}
                    ></Form.Check>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="KAWASAN PERBATASAN"
                      value=""
                      id="border_area"
                      {...register("border_area")}
                      // {...register('border_area', { required: 'Kondisi fondasi wajib dipilih' })}
                      // isInvalid={!!errors.border_area}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="KAWASAN PESISIR/NELAYAN"
                      value=""
                      id="coastal_fisherman_area"
                      {...register("coastal_fisherman_area")}
                      // {...register('coastal_fisherman_area', { required: 'Kondisi fondasi wajib dipilih' })}
                      // isInvalid={!!errors.coastal_fisherman_area}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="KAWASAN RAWAN AIR"
                      value=""
                      id="water_prone_area"
                      {...register("water_prone_area")}
                      // {...register('water_prone_area', { required: 'Kondisi fondasi wajib dipilih' })}
                      // isInvalid={!!errors.water_prone_area}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="KAWASAN TRANSMIGRASI"
                      value=""
                      id="transmigration_area"
                      {...register("transmigration_area")}
                      // {...register('transmigration_area', { required: 'Kondisi fondasi wajib dipilih' })}
                      // isInvalid={!!errors.transmigration_area}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="KAWASAN EKONOMI KHUSUS"
                      value=""
                      id="special_economic_zone"
                      {...register("special_economic_zone")}
                      // {...register('special_economic_zone', { required: 'Kondisi fondasi wajib dipilih' })}
                      // isInvalid={!!errors.special_economic_zone}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="KSPN"
                      value=""
                      id="national_strategic_tourism"
                      {...register("national_strategic_tourism")}
                      // {...register('national_strategic_tourism', { required: 'Kondisi fondasi wajib dipilih' })}
                      // isInvalid={!!errors.national_strategic_tourism}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="PULAU-PULAU KECIL/TERLUAR"
                      value=""
                      id="small_outermost_islands"
                      {...register("small_outermost_islands")}
                      // {...register('small_outermost_islands', { required: 'Kondisi fondasi wajib dipilih' })}
                      // isInvalid={!!errors.small_outermost_islands}
                    />

                    <Form.Control.Feedback type="invalid">
                      {errors.outsider_area?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Fungsi Ruang *</Form.Label>
                    <Form.Select {...register("room_function")}>
                      <option value="">Pilih...</option>
                      <option value="PERUMAHAN">PERUMAHAN</option>
                      <option value="NON PERUMAHAN">NON PERUMAHAN</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <div className="d-flex gap-2 align-items-center">
                      <Form.Label className="mb-0">Latitude</Form.Label>
                      <div className="d-flex gap-2 align-items-center">
                        {/* <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="tooltip-location">Ambil Lokasi</Tooltip>}
                          >
                            <Button size="sm" variant=" p-1" onClick={handleGetLocation}>
                              📍
                            </Button>
                          </OverlayTrigger> */}

                        {/* <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="tooltip-map">Lihat di Map</Tooltip>}
                          > */}
                        <Button
                          size="sm"
                          variant="mx-0 my-0"
                          onClick={handleOpenMapModal}
                        >
                          🌍 Lihat di Map
                        </Button>
                        {/* </OverlayTrigger> */}

                        {/* GPS Status Indicator */}
                        <div className="d-flex align-items-center ms-2">
                          <span
                            style={{
                              display: "inline-block",
                              width: "10px",
                              height: "10px",
                              borderRadius: "50%",
                              marginRight: "6px",
                              backgroundColor:
                                gpsStatus === "active"
                                  ? "green"
                                  : gpsStatus === "inactive"
                                  ? "red"
                                  : "gray",
                            }}
                          ></span>
                          <small>
                            {gpsStatus === "active"
                              ? "GPS Aktif"
                              : gpsStatus === "inactive"
                              ? "GPS Mati"
                              : "Cek GPS..."}
                          </small>
                        </div>
                      </div>
                    </div>

                    <Form.Control
                      type="text"
                      {...register("latitude")}
                      isInvalid={!!errors.latitude}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.latitude?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Longitude</Form.Label>
                    <Form.Control type="text" {...register("longitude")} />
                  </Form.Group>

                  {/* <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Fungsi Ruang *</Form.Label>
                    <Form.Select
                      {...register('room_function', { required: 'Fungsi ruang wajib dipilih' })}
                      isInvalid={!!errors.room_function}
                    >
                      <option value="">Pilih...</option>
                      <option value="PERUMAHAN">PERUMAHAN</option>
                      <option value="NON PERUMAHAN">NON PERUMAHAN</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.room_function?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                  
              <Form.Group className="mb-3">
                <Form.Label>Latitude</Form.Label>
                <Form.Control
                  type="text"
                  {...register('latitude', { 
                    pattern: {
                      value: /^([0-9]{1,2})(\.[0-9]+)?$/,
                      message: 'Nilai Latitude antara 0.0000000 s.d. 90.00000000'
                    }
                  })}
                  isInvalid={!!errors.latitude}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.latitude?.message}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Longitude</Form.Label>
                <Form.Control
                  type="text"
                  {...register('longitude', { 
                    pattern: {
                      value: /^([0-9]{1,3})(\.[0-9]+)?$/,
                      message: 'Nilai Longitude antara 0.00000000 s.d. 180.00000000'
                    }
                  })}
                  isInvalid={!!errors.longitude}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.longitude?.message}
                </Form.Control.Feedback>
              </Form.Group>
 */}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Card className="mb-3">
            <Card.Header>
              <h5>DATA TEKNIS BANGUNAN</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Kondisi Fondasi *
                      <HelpIcon
                        onClick={() => fetchHelp("housing_form", "FONDASI")}
                      />
                    </Form.Label>
                    <Form.Select {...register("foundation")}>
                      <option value="">Pilih...</option>
                      <option value="LAYAK">LAYAK</option>
                      <option value="MENUJU LAYAK">MENUJU LAYAK</option>
                      <option value="AGAK LAYAK">AGAK LAYAK</option>
                      <option value="KURANG LAYAK">KURANG LAYAK</option>
                      <option value="TIDAK LAYAK">TIDAK LAYAK</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      Kondisi Sloof *
                      <HelpIcon
                        onClick={() => fetchHelp("housing_form", "SLOOF")}
                      />
                    </Form.Label>
                    <Form.Select {...register("sloof_condition")}>
                      <option value="">Pilih...</option>
                      <option value="LAYAK">LAYAK</option>
                      <option value="MENUJU LAYAK">MENUJU LAYAK</option>
                      <option value="AGAK LAYAK">AGAK LAYAK</option>
                      <option value="KURANG LAYAK">KURANG LAYAK</option>
                      <option value="TIDAK LAYAK">TIDAK LAYAK</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      Kondisi Kolom *
                      <HelpIcon
                        onClick={() => fetchHelp("housing_form", "KOLOM")}
                      />
                    </Form.Label>
                    <Form.Select {...register("column_condition")}>
                      <option value="">Pilih...</option>
                      <option value="LAYAK">LAYAK</option>
                      <option value="MENUJU LAYAK">MENUJU LAYAK</option>
                      <option value="AGAK LAYAK">AGAK LAYAK</option>
                      <option value="KURANG LAYAK">KURANG LAYAK</option>
                      <option value="TIDAK LAYAK">TIDAK LAYAK</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      Kondisi Ring Balok *
                      <HelpIcon
                        onClick={() => fetchHelp("housing_form", "RINGBALOK")}
                      />
                    </Form.Label>
                    <Form.Select {...register("beam_condition")}>
                      <option value="">Pilih...</option>
                      <option value="LAYAK">LAYAK</option>
                      <option value="MENUJU LAYAK">MENUJU LAYAK</option>
                      <option value="AGAK LAYAK">AGAK LAYAK</option>
                      <option value="KURANG LAYAK">KURANG LAYAK</option>
                      <option value="TIDAK LAYAK">TIDAK LAYAK</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Kondisi Rangka Atap *</Form.Label>
                    <Form.Select {...register("roof_frame_condition")}>
                      <option value="">Pilih...</option>
                      <option value="LAYAK">LAYAK</option>
                      <option value="MENUJU LAYAK">MENUJU LAYAK</option>
                      <option value="AGAK LAYAK">AGAK LAYAK</option>
                      <option value="KURANG LAYAK">KURANG LAYAK</option>
                      <option value="TIDAK LAYAK">TIDAK LAYAK</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      Kondisi Atap *
                      <HelpIcon
                        onClick={() => fetchHelp("housing_form", "ATAP")}
                      />
                    </Form.Label>
                    <Form.Select {...register("roof_condition")}>
                      <option value="">Pilih...</option>
                      <option value="LAYAK">LAYAK</option>
                      <option value="MENUJU LAYAK">MENUJU LAYAK</option>
                      <option value="AGAK LAYAK">AGAK LAYAK</option>
                      <option value="KURANG LAYAK">KURANG LAYAK</option>
                      <option value="TIDAK LAYAK">TIDAK LAYAK</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Material Atap *</Form.Label>
                    <Form.Select {...register("roof_material")}>
                      <option value="">Pilih...</option>
                      <option value="GENTENG">GENTENG</option>
                      <option value="SENG">SENG</option>
                      <option value="KAYU/SIRAP">KAYU/SIRAP</option>
                      <option value="IJUK">IJUK</option>
                      <option value="JERAMI">JERAMI</option>
                      <option value="RUMBIA">RUMBIA</option>
                      <option value="BAMBU">BAMBU</option>
                      <option value="DAUN">DAUN</option>
                      <option value="ASBES">ASBES</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Kondisi Lantai *
                      <HelpIcon
                        onClick={() => fetchHelp("housing_form", "LANTAI")}
                      />
                    </Form.Label>
                    <Form.Select {...register("floor_condition")}>
                      <option value="">Pilih...</option>
                      <option value="LAYAK">LAYAK</option>
                      <option value="MENUJU LAYAK">MENUJU LAYAK</option>
                      <option value="AGAK LAYAK">AGAK LAYAK</option>
                      <option value="KURANG LAYAK">KURANG LAYAK</option>
                      <option value="TIDAK LAYAK">TIDAK LAYAK</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Material Lantai *</Form.Label>
                    <Form.Select {...register("floor_material")}>
                      <option value="">Pilih...</option>
                      <option value="MARMER/GRANIT">MARMER/GRANIT</option>
                      <option value="KERAMIK">KERAMIK</option>
                      <option value="UBIN/TEGEL">UBIN/TEGEL</option>
                      <option value="PLESTERAN">PLESTERAN</option>
                      <option value="KAYU">KAYU</option>
                      <option value="BAMBU">BAMBU</option>
                      <option value="TANAH">TANAH</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      Kondisi Dinding *
                      <HelpIcon
                        onClick={() => fetchHelp("housing_form", "DINDING")}
                      />
                    </Form.Label>
                    <Form.Select {...register("wall_condition")}>
                      <option value="">Pilih...</option>
                      <option value="LAYAK">LAYAK</option>
                      <option value="MENUJU LAYAK">MENUJU LAYAK</option>
                      <option value="AGAK LAYAK">AGAK LAYAK</option>
                      <option value="KURANG LAYAK">KURANG LAYAK</option>
                      <option value="TIDAK LAYAK">TIDAK LAYAK</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Material Dinding *</Form.Label>
                    <Form.Select {...register("wall_material")}>
                      <option value="">Pilih...</option>
                      <option value="TEMBOK">TEMBOK</option>
                      <option value="KAYU">KAYU</option>
                      <option value="PLESTERAN ANYAMAN BAMBU">
                        PLESTERAN ANYAMAN BAMBU
                      </option>
                      <option value="BAMBU">BAMBU</option>
                      <option value="ANYAMAN BAMBU">ANYAMAN BAMBU</option>
                      <option value="GRC">GRC</option>
                      <option value="RUMBIA">RUMBIA</option>
                      <option value="LAINNYA">LAINNYA</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      Jendela *
                      <HelpIcon
                        onClick={() => fetchHelp("housing_form", "JENDELA")}
                      />
                    </Form.Label>
                    <Form.Select {...register("window")}>
                      <option value="">Pilih...</option>
                      <option value="LAYAK">LAYAK</option>
                      <option value="MENUJU LAYAK">MENUJU LAYAK</option>
                      <option value="AGAK LAYAK">AGAK LAYAK</option>
                      <option value="KURANG LAYAK">KURANG LAYAK</option>
                      <option value="TIDAK LAYAK">TIDAK LAYAK</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      Ventilasi *
                      <HelpIcon
                        onClick={() => fetchHelp("housing_form", "VENTILASI")}
                      />
                    </Form.Label>
                    <Form.Select {...register("ventilation")}>
                      <option value="">Pilih...</option>
                      <option value="LAYAK">LAYAK</option>
                      <option value="MENUJU LAYAK">MENUJU LAYAK</option>
                      <option value="AGAK LAYAK">AGAK LAYAK</option>
                      <option value="KURANG LAYAK">KURANG LAYAK</option>
                      <option value="TIDAK LAYAK">TIDAK LAYAK</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              {/* <Row>
        <Col md={12}>
          <Card className="mb-3">
            <Card.Header>
              <h5>DATA TEKNIS BANGUNAN</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Kondisi Fondasi *
                       <HelpIcon onClick={() => fetchHelp("housing_form", "FONDASI")} />
                    </Form.Label>
                    <Form.Select
                      {...register('foundation', { required: 'Kondisi fondasi wajib dipilih' })}
                      isInvalid={!!errors.foundation}
                    >
                      <option value="">Pilih...</option>
                      <option value="LAYAK">LAYAK</option>
                      <option value="MENUJU LAYAK">MENUJU LAYAK</option>
                      <option value="AGAK LAYAK">AGAK LAYAK</option>
                      <option value="KURANG LAYAK">KURANG LAYAK</option>
                      <option value="TIDAK LAYAK">TIDAK LAYAK</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.foundation?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Kondisi Kolom *
                                             <HelpIcon onClick={() => fetchHelp("housing_form", "KOLOM")} />
                    </Form.Label>
                    <Form.Select
                      {...register('column_condition', { required: 'Kondisi kolom wajib dipilih' })}
                      isInvalid={!!errors.column_condition}
                    >
                      <option value="">Pilih...</option>
                      <option value="LAYAK">LAYAK</option>
                      <option value="MENUJU LAYAK">MENUJU LAYAK</option>
                      <option value="AGAK LAYAK">AGAK LAYAK</option>
                      <option value="KURANG LAYAK">KURANG LAYAK</option>
                      <option value="TIDAK LAYAK">TIDAK LAYAK</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.column_condition?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Kondisi Kuda-kuda Atap *</Form.Label>
                    <Form.Select
                      {...register('roof_frame_condition', { required: 'Kondisi kuda-kuda atap wajib dipilih' })}
                      isInvalid={!!errors.roof_frame_condition}
                    >
                      <option value="">Pilih...</option>
                      <option value="LAYAK">LAYAK</option>
                      <option value="MENUJU LAYAK">MENUJU LAYAK</option>
                      <option value="AGAK LAYAK">AGAK LAYAK</option>
                      <option value="KURANG LAYAK">KURANG LAYAK</option>
                      <option value="TIDAK LAYAK">TIDAK LAYAK</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.roof_frame_condition?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Kondisi Atap *
                      <HelpIcon onClick={() => fetchHelp("housing_form", "ATAP")} />

                    </Form.Label>
                    <Form.Select
                      {...register('roof_condition', { required: 'Kondisi atap wajib dipilih' })}
                      isInvalid={!!errors.roof_condition}
                    >
                      <option value="">Pilih...</option>
                      <option value="LAYAK">LAYAK</option>
                      <option value="MENUJU LAYAK">MENUJU LAYAK</option>
                      <option value="AGAK LAYAK">AGAK LAYAK</option>
                      <option value="KURANG LAYAK">KURANG LAYAK</option>
                      <option value="TIDAK LAYAK">TIDAK LAYAK</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.roof_condition?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Material Atap *</Form.Label>
                    <Form.Select
                      {...register('roof_material', { required: 'Material atap wajib dipilih' })}
                      isInvalid={!!errors.roof_material}
                    >
                      <option value="">Pilih...</option>
                      <option value="GENTENG">GENTENG</option>
                      <option value="SENG">SENG</option>
                      <option value="KAYU/SIRAP">KAYU/SIRAP</option>
                      <option value="IJUK">IJUK</option>
                      <option value="JERAMI">JERAMI</option>
                      <option value="RUMBIA">RUMBIA</option>
                      <option value="BAMBU">BAMBU</option>
                      <option value="DAUN">DAUN</option>
                      <option value="ASBES">ASBES</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.roof_material?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Kondisi Dinding *
                       <HelpIcon onClick={() => fetchHelp("housing_form", "DINDING")} />
                    </Form.Label>
                    <Form.Select
                      {...register('wall_condition', { required: 'Kondisi dinding wajib dipilih' })}
                      isInvalid={!!errors.wall_condition}
                    >
                      <option value="">Pilih...</option>
                      <option value="LAYAK">LAYAK</option>
                      <option value="MENUJU LAYAK">MENUJU LAYAK</option>
                      <option value="AGAK LAYAK">AGAK LAYAK</option>
                      <option value="KURANG LAYAK">KURANG LAYAK</option>
                      <option value="TIDAK LAYAK">TIDAK LAYAK</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.wall_condition?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Material Dinding *</Form.Label>
                    <Form.Select
                      {...register('wall_material', { required: 'Material dinding wajib dipilih' })}
                      isInvalid={!!errors.wall_material}
                    >
                      <option value="">Pilih...</option>
                      <option value="TEMBOK">TEMBOK</option>
                      <option value="KAYU">KAYU</option>
                      <option value="PLESTERAN ANYAMAN BAMBU">PLESTERAN ANYAMAN BAMBU</option>
                      <option value="BAMBU">BAMBU</option>
                      <option value="ANYAMAN BAMBU">ANYAMAN BAMBU</option>
                      <option value="GRC">GRC</option>
                      <option value="RUMBIA">RUMBIA</option>
                      <option value="LAINNYA">LAINNYA</option>

                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.wall_material?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Kondisi Lantai *
                       <HelpIcon onClick={() => fetchHelp("housing_form", "LANTAI")} />
                    </Form.Label>
                    <Form.Select
                      {...register('floor_condition', { required: 'Kondisi lantai wajib dipilih' })}
                      isInvalid={!!errors.floor_condition}
                    >
                      <option value="">Pilih...</option>
                      <option value="LAYAK">LAYAK</option>
                      <option value="MENUJU LAYAK">MENUJU LAYAK</option>
                      <option value="AGAK LAYAK">AGAK LAYAK</option>
                      <option value="KURANG LAYAK">KURANG LAYAK</option>
                      <option value="TIDAK LAYAK">TIDAK LAYAK</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.floor_condition?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Material Lantai *</Form.Label>
                    <Form.Select
                      {...register('floor_material', { required: 'Material lantai wajib dipilih' })}
                      isInvalid={!!errors.floor_material}
                    >
                      <option value="">Pilih...</option>
                      <option value="MARMER/GRANIT">MARMER/GRANIT</option>
                      <option value="KERAMIK">KERAMIK</option>
                      <option value="UBIN/TEGEL">UBIN/TEGEL</option>
                      <option value="PLESTERAN">PLESTERAN</option>
                      <option value="KAYU">KAYU</option>
                      <option value="BAMBU">BAMBU</option>
                      <option value="TANAH">TANAH</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.floor_material?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Jendela *
                      <HelpIcon onClick={() => fetchHelp("housing_form", "JENDELA")} />
                    </Form.Label>
                    <Form.Select
                      {...register('window', { required: 'Jendela wajib dipilih' })}
                      isInvalid={!!errors.window}
                    >
                      <option value="">Pilih...</option>
                      <option value="LAYAK">LAYAK</option>
                      <option value="MENUJU LAYAK">MENUJU LAYAK</option>
                      <option value="AGAK LAYAK">AGAK LAYAK</option>
                      <option value="KURANG LAYAK">KURANG LAYAK</option>
                      <option value="TIDAK LAYAK">TIDAK LAYAK</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.window?.message}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Ventilasi *
                       <HelpIcon onClick={() => fetchHelp("housing_form", "VENTILASI")} />
                    </Form.Label>
                    <Form.Select
                      {...register('ventilation', { required: 'Ventilasi wajib dipilih' })}
                      isInvalid={!!errors.ventilation}
                    >
                      <option value="">Pilih...</option>
                      <option value="LAYAK">LAYAK</option>
                      <option value="MENUJU LAYAK">MENUJU LAYAK</option>
                      <option value="AGAK LAYAK">AGAK LAYAK</option>
                      <option value="KURANG LAYAK">KURANG LAYAK</option>
                      <option value="TIDAK LAYAK">TIDAK LAYAK</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.ventilation?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
 */}

              {/* <Form.Group className="mb-3">
                <Form.Label>Foto Rumah Sebelum Perbaikan</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  {...register("photo_before_repair")}
                  onChange={handlePhotoChange}
                />
                {photoPreview && (
                  <div className="mt-2">
                    <img
                      src={photoPreview}
                      alt="Preview"
                      style={{ maxWidth: "200px", maxHeight: "200px" }}
                      className="img-thumbnail"
                    />
                  </div>
                )}
              </Form.Group> */}
              {/* <Form.Group className="mb-3">
                <Form.Label>Foto</Form.Label>
                <PictureEmbed
                  pictures={pictures}
                  setPictures={setPictures}
                  housingId={housingId}
                  pendingPhotos={pendingPhotos}
                  setPendingPhotos={setPendingPhotos}
                  isMobile={isMobile}
                />
              </Form.Group> */}
            </Card.Body>
          </Card>

          <Modal
            show={showMapModal}
            onHide={() => setShowMapModal(false)}
            size="lg"
          >
            <Modal.Header closeButton>
              <Modal.Title>Lokasi Rumah</Modal.Title>
              <div className="ms-auto">
                <select
                  className="form-select form-select-sm"
                  style={{ width: "180px" }}
                  value={activeBase}
                  onChange={(e) => setActiveBase(e.target.value)}
                >
                  {Object.entries(baseLayers).map(([key, layer]) => (
                    <option key={key} value={key}>
                      {layer.name}
                    </option>
                  ))}
                </select>
              </div>
            </Modal.Header>

            <Modal.Body style={{ height: "500px" }}>
              {currentLocation && (
                <MapContainer
                  center={[currentLocation.lat, currentLocation.lng]}
                  zoom={16}
                  style={{ height: "100%", width: "100%" }}
                >
                  {activeBase === "hybrid" ? (
                    <>
                      <TileLayer
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                        attribution="© Esri"
                      />
                      <TileLayer
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                        attribution="© Esri"
                      />
                    </>
                  ) : (
                    <TileLayer
                      url={baseLayers[activeBase].url}
                      attribution={baseLayers[activeBase].attribution}
                    />
                  )}
                  <DraggableMarker
                    position={currentLocation}
                    setPosition={(pos) => {
                      setCurrentLocation(pos);
                      setValue("latitude", pos.lat.toFixed(8));
                      setValue("longitude", pos.lng.toFixed(8));
                    }}
                  />
                </MapContainer>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowMapModal(false)}
              >
                Tutup
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal show={show} onHide={() => setShow(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>Bantuan</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {content.map((row) => (
                <p key={row.item_key}>
                  <strong>{row.item_key}:</strong> {row.help_text}
                </p>
              ))}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShow(false)}>
                Tutup
              </Button>
            </Modal.Footer>
          </Modal>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <Card className="mb-3">
            <Card.Header>
              <h5>LUAS BANGUNAN DAN TANAH</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Luas Lantai (M²)*</Form.Label>
                    <Form.Control type="text" {...register("house_area_m2")} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Luas Tanah (M²)*</Form.Label>
                    <Form.Control type="text" {...register("land_area_m2")} />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Card className="mb-3">
            <Card.Header>
              <h5>AKSES AIR MINUM</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Sumber Air Minum *</Form.Label>
                    <Form.Select {...register("drinking_water_source")}>
                      <option value="">Pilih...</option>
                      <option value="PERPIPAAN">PERPIPAAN</option>
                      <option value="AIR KEMASAN/ISI ULANG">
                        AIR KEMASAN/ISI ULANG
                      </option>
                      <option value="SUMUR">SUMUR</option>
                      <option value="MATA AIR">MATA AIR</option>
                      <option value="AIR HUJAN">AIR HUJAN</option>
                      <option value="SUNGAI/DANAU">SUNGAI/DANAU</option>
                      <option value="LAINNYA">LAINNYA</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Jarak ke Pembuangan Tinja</Form.Label>
                    <Form.Select {...register("distance_to_sewage")}>
                      <option value="">Pilih...</option>
                      <option value="< 10 M">KURANG DARI 10 METER</option>
                      <option value="> 10 M">10 METER ATAU LEBIH</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <Card className="mb-3">
            <Card.Header>
              <h5>AKSES SANITASI</h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Fasilitas BAB *</Form.Label>
                    <Form.Select {...register("bathroom_toilet")}>
                      <option value="">Pilih...</option>
                      <option value="SENDIRI">MILIK SENDIRI</option>
                      <option value="BERSAMA/MCK KOMUNAL">
                        MILIK BERSAMA/KOMUNITAS
                      </option>
                      <option value="TIDAK ADA">TIDAK ADA</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Jenis Jamban/kloset *</Form.Label>
                    <Form.Select {...register("toilet_type")}>
                      <option value="">Pilih...</option>
                      <option value="LEHER ANGSA">LEHER ANGSA</option>
                      <option value="PLENGSENGAN">PLENGSENGAN</option>
                      <option value="CEMPLUNG/CUBLUK">CEMPLUNG/CUBLUK</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>TPA Tinja *</Form.Label>
                    <Form.Select {...register("sewage_disposal_type")}>
                      <option value="">Pilih...</option>
                      <option value="TANGKI SEPTIK">TANGKI SEPTIK</option>
                      <option value="IPAL">IPAL</option>
                      <option value="KOLAM/SAWAH/SUNGAI/DANAU/LAUT">
                        KOLAM/SAWAH/SUNGAI/DANAU/LAUT
                      </option>
                      <option value="LUBANG TANAH">LUBANG TANAH</option>
                      <option value="PANTAI/TANAH LAPANG/KEBUN">
                        PANTAI/TANAH LAPANG/KEBUN
                      </option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div className="d-flex justify-content-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
          Batal
        </Button>
        <Button variant="primary" type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Menyimpan..." : housing ? "Update" : "Simpan"}
        </Button>
      </div>
    </Form>
  );
};

export default HousingForm;
